import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/images/sulthaniya_logo.png';
import { IconDashboard, IconUsers, IconRoles, IconContent, IconPosts, IconBooks, IconHomeTabs, IconAnnouncements, IconFeedback, IconAnalytics, IconQuotes, IconAudio, IconVideo, IconSettings, IconDataDeletion, IconSignOut } from './Icons';

const NAV_TOP = [
  { to: '/',      Icon: IconDashboard, label: 'Dashboard' },
  { to: '/users', Icon: IconUsers,     label: 'Users' },
  { to: '/roles', Icon: IconRoles,     label: 'Roles' },
];

// Grouped under the "Contents" dropdown to keep the sidebar short enough
// that Sign out never gets pushed below the fold.
const CONTENTS_GROUP = [
  { to: '/content',   Icon: IconContent,  label: 'Documents' },
  { to: '/posts',     Icon: IconPosts,    label: 'Posts' },
  { to: '/books',     Icon: IconBooks,    label: 'Books' },
  { to: '/home-tabs', Icon: IconHomeTabs, label: 'Home Tabs' },
  { to: '/quotes',    Icon: IconQuotes,   label: 'Quotes' },
  { to: '/audio',     Icon: IconAudio,    label: 'Audio' },
  { to: '/video',     Icon: IconVideo,    label: 'Video' },
];

const NAV_BOTTOM = [
  { to: '/announcements', Icon: IconAnnouncements, label: 'Announcements' },
  { to: '/feedback',      Icon: IconFeedback,      label: 'Feedback' },
  { to: '/data-deletion', Icon: IconDataDeletion,  label: 'Data Deletion' },
  { to: '/analytics',     Icon: IconAnalytics,     label: 'Analytics' },
  { to: '/settings',      Icon: IconSettings,      label: 'Site Settings' },
];

function NavItem({ to, Icon, label, end, indent }) {
  return (
    <NavLink key={to} to={to} end={end} style={({ isActive }) => ({
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '9px 12px', paddingLeft: indent ? 34 : 12, borderRadius: 8, marginBottom: 2,
      color: isActive ? '#fff' : '#7fc8a0',
      background: isActive ? '#ffffff18' : 'transparent',
      fontSize: 13, fontWeight: isActive ? 500 : 400,
      transition: 'all .15s', textDecoration: 'none',
    })}>
      <span style={{ display: 'flex', alignItems: 'center', opacity: .9 }}><Icon /></span>
      {label}
    </NavLink>
  );
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isInContentsGroup = CONTENTS_GROUP.some(item => location.pathname.startsWith(item.to));
  const [contentsOpen, setContentsOpen] = useState(isInContentsGroup);

  // Keep the group expanded when navigation lands on one of its routes by
  // any means other than clicking the toggle itself (e.g. a dashboard link).
  useEffect(() => {
    if (isInContentsGroup) setContentsOpen(true);
  }, [isInContentsGroup]);

  return (
    <aside style={{
      width: 220, minWidth: 220,
      background: 'var(--accent2)',
      display: 'flex', flexDirection: 'column',
      height: '100vh', position: 'sticky', top: 0,
    }}>
      {/* Logo */}
      <div style={{ padding: '20px 20px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src={logo} alt="Sulthaniya Foundation" style={{ width: 38, height: 38, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}/>
          <div>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 15, color: '#fff', lineHeight: 1.2, fontWeight: 700 }}>
              Sulthaniya
            </div>
            <div style={{ fontSize: 9, color: '#a8d5ba', letterSpacing: '.12em', textTransform: 'uppercase', marginTop: 1 }}>
              Foundation Admin
            </div>
          </div>
        </div>
        <div style={{ height: 1, background: '#ffffff18', marginTop: 18 }}/>
      </div>

      {/* Nav — scrolls independently so Sign out below never gets pushed
          off-screen, regardless of how many items or the Contents expand state */}
      <nav style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 10px' }}>
        {NAV_TOP.map(({ to, Icon, label }) => (
          <NavItem key={to} to={to} Icon={Icon} label={label} end={to === '/'} />
        ))}

        <button
          onClick={() => setContentsOpen(o => !o)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 10,
            padding: '9px 12px', borderRadius: 8, marginBottom: 2,
            color: isInContentsGroup ? '#fff' : '#7fc8a0',
            background: isInContentsGroup ? '#ffffff18' : 'transparent',
            fontSize: 13, fontWeight: isInContentsGroup ? 500 : 400,
            border: 'none', cursor: 'pointer', font: 'inherit', textAlign: 'left',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', opacity: .9 }}><IconContent /></span>
          Contents
          <span style={{
            marginLeft: 'auto', display: 'flex', transition: 'transform .15s',
            transform: contentsOpen ? 'rotate(90deg)' : 'rotate(0deg)',
          }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </button>
        {contentsOpen && CONTENTS_GROUP.map(({ to, Icon, label }) => (
          <NavItem key={to} to={to} Icon={Icon} label={label} indent />
        ))}

        {NAV_BOTTOM.map(({ to, Icon, label }) => (
          <NavItem key={to} to={to} Icon={Icon} label={label} />
        ))}
      </nav>

      {/* User */}
      <div style={{ padding: '16px 14px', borderTop: '1px solid #ffffff18' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: '50%',
            background: '#ffffff22', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 13, fontWeight: 600, color: '#fff', flexShrink: 0,
          }}>
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name}</div>
            <div style={{ fontSize: 10, color: '#7fc8a0' }}>Administrator</div>
          </div>
        </div>
        <button onClick={logout} style={{
          width: '100%', background: '#ffffff12', border: 'none',
          borderRadius: 7, padding: '7px 10px', color: '#a8d5ba',
          fontSize: 12, cursor: 'pointer', transition: 'background .15s',
          display: 'flex', alignItems: 'center', gap: 7,
        }}>
          <IconSignOut /> Sign out
        </button>
      </div>
    </aside>
  );
}
